/**
 * Cash book and bank book.
 *
 * Not a ledger statement of one account: all the cash (or bank) accounts
 * together, money in and money out in their own columns, a running
 * balance, and a total for every day -- the figure a cashier counts the
 * drawer against. Pick one account to see that till alone.
 *
 * The server decides which accounts a user may see: a cashier gets their
 * own till, all of it, because the till's balance is what they answer for.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Chip, MenuItem, Skeleton, Stack, Tab, Tabs, TextField,
  Typography, useMediaQuery,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { fetchCashBook } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { CashBookRow } from '../api/types'
import { useLedgers } from '../store/LedgerCache'
import { c, fmtMoney } from '../theme'
import { sidedText } from '../vouchers/money'
import { Figure, FigureGrid } from './SummaryFigures'
import { useBi } from '../components/Bi'
import ReportShell, { rangeLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import ReportToolbar from './ReportToolbar'
import VoucherDrawer from './VoucherDrawer'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import { gregorian } from './hijri'

type Kind = 'cash' | 'bank'

const blankIfZero = (v: string) => (Number(v) === 0 ? '' : fmtMoney(v))

export default function CashBook({ kind: initial = 'cash' }: { kind?: Kind }) {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const { range, setRange, presets } = useDateRange()
  const [kind, setKind] = useState<Kind>(initial)
  const [account, setAccount] = useState('')
  const [open, setOpen] = useState<number | null>(null)
  const narrow = useMediaQuery('(max-width:760px)')
  const { ledgers } = useLedgers()

  const choices = useMemo(() => ledgers.filter((l) =>
    (l.group_keys ?? [l.group_key]).includes(kind === 'cash' ? 'CASH' : 'BANK')),
  [ledgers, kind])

  const q = useQuery({
    queryKey: ['cash-book', kind, account, range.from, range.to],
    queryFn: () => fetchCashBook({ kind, account: account || undefined,
                                   from_date: range.from, to_date: range.to }),
    retry: false,
  })
  const b = q.data
  const many = (b?.accounts.length ?? 0) > 1
  const rows = b?.days.flatMap((d) => d.rows) ?? []

  const file = `${kind}-book${account ? `-${account}` : ''}-${range.from}-${range.to}`

  return (
    <>
      <ReportShell
        titleKey={kind === 'cash' ? 'nav.cashBook' : 'nav.bankBook'}
        period={rangeLabel(range.from, range.to, i18n.language, t)}
        headRight={b ? <Chip size="small" variant="outlined"
          label={rows.length} /> : undefined}
        toolbar={
          <ReportToolbar range={range} setRange={setRange} presets={presets}
            pdf={{ path: 'cash-book.pdf',
                   params: { kind, account: account || undefined,
                             from_date: range.from, to_date: range.to },
                   filename: `${file}.pdf` }}
            onExport={() => downloadCsv(file, [
              ['Date', 'Voucher', 'Account', 'Particulars', 'Narration',
               'Reference', 'Receipts', 'Payments', 'Balance'],
              ...(b?.days ?? []).flatMap((d) => d.rows.map((r) => [
                d.date, r.voucher_no, r.account_code, r.particulars,
                r.narration ?? '', r.reference ?? '', r.receipt, r.payment,
                r.balance])),
            ])}>
            <Tabs value={kind}
              onChange={(_, v) => { setKind(v as Kind); setAccount('') }}
              sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}>
              <Tab value="cash" label={t('nav.cashBook')} />
              <Tab value="bank" label={t('nav.bankBook')} />
            </Tabs>
            <TextField select size="small" label={t('cashBook.account')}
              value={account} onChange={(e) => setAccount(e.target.value)}
              sx={{ minWidth: 220 }}>
              <MenuItem value="">{t('cashBook.allAccounts')}</MenuItem>
              {choices.map((l) => (
                <MenuItem key={l.code} value={l.code}>{l.code} {l.name_en}</MenuItem>
              ))}
            </TextField>
          </ReportToolbar>
        }>

        {b && (
          <FigureGrid>
            <Figure label={bi('cashBook.opening')} value={sidedText(String(b.opening))} />
            <Figure label={bi('cashBook.receipts')} value={fmtMoney(b.receipts)} />
            <Figure label={bi('cashBook.payments')} value={fmtMoney(b.payments)} />
            <Figure label={bi('cashBook.closing')} value={sidedText(String(b.closing))} strong />
          </FigureGrid>
        )}

        {q.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : q.isError ? (
          <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(q.error)}</Alert>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
            {t('report.nothingInRange')}
          </Typography>
        ) : narrow ? (
          <Box className="ruled">
            {b!.days.map((d) => (
              <Box key={d.date}>
                <Stack direction="row" justifyContent="space-between"
                  sx={{ px: 2, py: 0.75, bgcolor: c.surfacePage, fontSize: 12 }}>
                  <strong>{gregorian(d.date, i18n.language)}</strong>
                  <span className="num">{sidedText(String(d.closing))}</span>
                </Stack>
                {d.rows.map((r, i) => (
                  <Box key={i} onClick={() => setOpen(r.voucher_id)}
                    sx={{ px: 2, py: 1, cursor: 'pointer' }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: 13.5 }} noWrap>{r.particulars}</Typography>
                      <span className="num" style={{
                        color: Number(r.payment) ? c.dangerInk : undefined }}>
                        {Number(r.receipt) ? `+${fmtMoney(r.receipt)}`
                                           : `−${fmtMoney(r.payment)}`}
                      </span>
                    </Stack>
                    <Typography className="num" sx={{ fontSize: 11.5, color: c.inkMuted }}>
                      {r.voucher_no}{many ? ` · ${r.account_code}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ px: 2, pb: 1 }}>
            <table className="rpt">
              <thead>
                <tr>
                  <th style={{ width: 104 }}>{t('entry.date')}</th>
                  <th style={{ width: 132 }}>{t('report.voucherNo')}</th>
                  <th>{t('cashBook.particulars')}</th>
                  {many && <th style={{ width: 90 }}>{t('cashBook.account')}</th>}
                  <th className="num" style={{ width: 116 }}>{t('cashBook.receipts')}</th>
                  <th className="num" style={{ width: 116 }}>{t('cashBook.payments')}</th>
                  <th className="num" style={{ width: 124 }}>{t('cashBook.balance')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="sub">
                  <td colSpan={many ? 6 : 5}>{t('cashBook.opening')}</td>
                  <td className="num">{fmtMoney(b!.opening)}</td>
                </tr>
                {b!.days.map((d) => (
                  <DayRows key={d.date} day={d} many={many}
                    lang={i18n.language} onOpen={setOpen}
                    totalLabel={t('cashBook.dayTotal')} />
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={many ? 4 : 3}>{t('cashBook.closing')}</td>
                  <td className="num">{fmtMoney(b!.receipts)}</td>
                  <td className="num">{fmtMoney(b!.payments)}</td>
                  <td className="num">{sidedText(String(b!.closing))}</td>
                </tr>
              </tfoot>
            </table>
          </Box>
        )}

        {many && b && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography className="section-label" sx={{ mb: 0.5 }}>
              {t('cashBook.byAccount')}
            </Typography>
            <table className="rpt">
              <tbody>
                {b.accounts.map((a) => (
                  <tr key={a.code} className="clickable"
                    onClick={() => setAccount(a.code)}>
                    <td className="num" style={{ textAlign: 'start', width: 90 }}>{a.code}</td>
                    <td>{a.name_en}</td>
                    <td className="num">{sidedText(String(a.closing))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        <Typography variant="caption"
          sx={{ display: 'block', px: 2, pb: 2, color: c.inkMuted }}>
          {t('cashBook.note')}
        </Typography>
        <ReportFooter />
      </ReportShell>
      <VoucherDrawer voucherId={open} onClose={() => setOpen(null)} />
    </>
  )
}

function DayRows({ day, many, lang, onOpen, totalLabel }: {
  day: { date: string; receipts: string; payments: string; closing: string
         rows: CashBookRow[] }
  many: boolean; lang: string; onOpen: (id: number) => void; totalLabel: string
}) {
  return (
    <>
      {day.rows.map((r, i) => (
        <tr key={`${r.voucher_id}-${i}`} className="clickable"
          onClick={() => onOpen(r.voucher_id)}
          style={{ opacity: r.status === 'REVERSED' ? 0.55 : 1 }}>
          <td className="num" style={{ textAlign: 'start' }}>
            {i === 0 ? gregorian(day.date, lang) : ''}</td>
          <td className="num" style={{ textAlign: 'start',
            color: 'var(--ink-muted)', fontSize: '0.78rem' }}>{r.voucher_no}</td>
          <td>
            {r.particulars}
            {(r.reference || r.narration) && (
              <div style={{ fontSize: '0.76rem', color: 'var(--ink-muted)' }}>
                {[r.reference, r.narration].filter(Boolean).join(' · ')}
              </div>
            )}
          </td>
          {many && <td className="num" style={{ textAlign: 'start',
            fontSize: '0.78rem' }}>{r.account_code}</td>}
          <td className="num">{blankIfZero(r.receipt)}</td>
          <td className="num">
            {blankIfZero(r.payment)}</td>
          <td className="num">{sidedText(String(r.balance))}</td>
        </tr>
      ))}
      <tr className="sub">
        <td colSpan={many ? 4 : 3} style={{ fontSize: '0.78rem' }}>{totalLabel}</td>
        <td className="num">{fmtMoney(day.receipts)}</td>
        <td className="num">{fmtMoney(day.payments)}</td>
        <td className="num">{sidedText(String(day.closing))}</td>
      </tr>
    </>
  )
}
